/* global BigInt */
import React, { useState, useEffect } from "react";
import { connectToContract, listenToEvents } from "./CrownContract";
import { formatEther, parseEther } from "ethers";

const UserLoanActions = ({ provider }) => {
  const [activeTab, setActiveTab] = useState("create");
  const [contract, setContract] = useState(null);
  const [userAddress, setUserAddress] = useState("");
  const [userBalance, setUserBalance] = useState("0");
  const [amount, setAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [loanDuration, setLoanDuration] = useState("");
  const [borrowerLoans, setBorrowerLoans] = useState([]);
  const [userBorrowerLoans, setUserBorrowerLoans] = useState([]);
  const [lenderLoans, setLenderLoans] = useState([]);
  const [availableLoans, setAvailableLoans] = useState([]);
  const [error, setError] = useState("");
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));

  useEffect(() => {
    const initializeContract = async () => {
      try {
        const contractInstance = await connectToContract(provider);
        setContract(contractInstance);

        const signer = await provider.getSigner();
        const address = await signer.getAddress();
        setUserAddress(address);

        const balance = await contractInstance.balanceOf(address);
        setUserBalance(formatEther(balance));

        await Promise.all([
          fetchAvailableLoans(contractInstance, provider),
          fetchAllBorrowerLoans(contractInstance, provider),
          fetchLenderLoans(contractInstance, address, provider),
          fetchUserBorrowerLoans(contractInstance, address),
        ]);
      } catch (err) {
        setError("Failed to initialize contract: " + err.message);
        alert("Failed to initialize contract");
      }
    };

    if (provider) {
      initializeContract();
      const interval = setInterval(initializeContract, 10000); // Update every 10 seconds
      return () => clearInterval(interval);
    }
  }, [provider]);

  // Update currentTime every second for real-time interest calculation
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    if (!contract) return;

    const setupEvents = async () => {
      try {
        const cleanup = await listenToEvents(provider, ({ name, data }) => {
          if (name === "UserLoanCreated") {
            const [lender] = data;
            setAvailableLoans((prev) => {
              const exists = prev.some((loan) => loan.lender.toLowerCase() === lender.toLowerCase());
              if (!exists) {
                return [
                  ...prev,
                  {
                    lender,
                    amount: "0",
                    requiredCollateral: "0",
                    interestRate: "0",
                    loanDuration: "0",
                    isActive: false,
                  },
                ];
              }
              return prev;
            });
            alert(`New loan created by ${lender.slice(0, 6)}...`);
          } else if (name === "UserLoanActivated") {
            const [borrower] = data;
            const fetchLoanDetails = async () => {
              try {
                const lender = await contract.borrowerToLender(borrower);
                if (!lender || lender === "0x0000000000000000000000000000000000000000") return;
                const loanDetails = await contract.userLoans(lender);
                if (
                  loanDetails.isActive &&
                  loanDetails.lender !== "0x0000000000000000000000000000000000000000"
                ) {
                  const loanData = {
                    lender: loanDetails.lender,
                    borrower: loanDetails.borrower,
                    amount: formatEther(loanDetails.loanAmount),
                    collateral: formatEther(loanDetails.collateralTokens),
                    interestRate: Number(loanDetails.interestRate).toString(),
                    loanDuration: (Number(loanDetails.loanDuration) / 86400).toString(),
                    startTime: Number(loanDetails.startTime),
                  };
                  setBorrowerLoans((prev) => {
                    const filtered = prev.filter((l) => l.borrower.toLowerCase() !== borrower.toLowerCase());
                    return [...filtered, loanData];
                  });
                  if (borrower.toLowerCase() === userAddress.toLowerCase()) {
                    setUserBorrowerLoans([loanData]);
                  }
                  if (lender.toLowerCase() === userAddress.toLowerCase()) {
                    setLenderLoans((prev) => {
                      const filtered = prev.filter((l) => l.borrower.toLowerCase() !== borrower.toLowerCase());
                      return [...filtered, loanData];
                    });
                  }
                  setAvailableLoans((prev) =>
                    prev.map((loan) =>
                      loan.lender.toLowerCase() === lender.toLowerCase()
                        ? { ...loan, isActive: true }
                        : loan
                    )
                  );
                }
              } catch (err) {
                console.error("Error processing UserLoanActivated:", err);
              }
            };
            fetchLoanDetails();
            alert(`Loan activated for borrower ${borrower.slice(0, 6)}...`);
          } else if (name === "UserLoanCancelled") {
            const [lender] = data;
            setAvailableLoans((prev) =>
              prev.filter((loan) => loan.lender.toLowerCase() !== lender.toLowerCase())
            );
            setLenderLoans((prev) =>
              prev.filter((loan) => loan.lender.toLowerCase() !== lender.toLowerCase())
            );
            alert(`Loan cancelled by ${lender.slice(0, 6)}...`);
          } else if (name === "UserLoanRepaid") {
            const [borrower, lenderAddress] = data;
            setBorrowerLoans((prev) =>
              prev.filter((loan) => loan.borrower.toLowerCase() !== borrower.toLowerCase())
            );
            setUserBorrowerLoans((prev) =>
              prev.filter((loan) => loan.borrower.toLowerCase() !== borrower.toLowerCase())
            );
            setLenderLoans((prev) =>
              prev.filter((loan) => loan.lender.toLowerCase() !== lenderAddress.toLowerCase())
            );
            alert(`Loan repaid by ${borrower.slice(0, 6)}...`);
          } else if (name === "UserLoanLiquidated") {
            const [borrower, lender] = data;
            setBorrowerLoans((prev) =>
              prev.filter((loan) => loan.borrower.toLowerCase() !== borrower.toLowerCase())
            );
            setUserBorrowerLoans((prev) =>
              prev.filter((loan) => loan.borrower.toLowerCase() !== borrower.toLowerCase())
            );
            setLenderLoans((prev) =>
              prev.filter((loan) => loan.lender.toLowerCase() !== lender.toLowerCase())
            );
            alert(`Loan liquidated for borrower ${borrower.slice(0, 6)}...`);
          }
        });
        return cleanup;
      } catch (err) {
        setError("Failed to set up event listeners: " + err.message);
        alert("Failed to set up event listeners");
      }
    };

    setupEvents();
  }, [contract, provider, userAddress]);

  const fetchAvailableLoans = async (contractInstance, provider) => {
    try {
      const fromBlock = 0;
      const toBlock = "latest";

      const createdEvents = await contractInstance.queryFilter(
        contractInstance.filters.UserLoanCreated(),
        fromBlock,
        toBlock
      );

      const activatedEvents = await contractInstance.queryFilter(
        contractInstance.filters.UserLoanActivated(),
        fromBlock,
        toBlock
      );
      const activatedLenders = new Set(
        activatedEvents
          .map((event) => event.args.lender?.toLowerCase())
          .filter((lender) => lender)
      );

      const loans = [];
      for (const event of createdEvents) {
        const { lender } = event.args;
        if (!lender || activatedLenders.has(lender.toLowerCase())) continue;
        const loanDetails = await contractInstance.userLoans(lender);
        if (
          BigInt(loanDetails.loanAmount) > 0 &&
          !loanDetails.isActive &&
          loanDetails.lender !== "0x0000000000000000000000000000000000000000"
        ) {
          const existingLoan = loans.find((loan) => loan.lender.toLowerCase() === lender.toLowerCase());
          if (!existingLoan) {
            loans.push({
              lender,
              amount: formatEther(loanDetails.loanAmount),
              requiredCollateral: formatEther((BigInt(loanDetails.loanAmount) * 130n) / 100n),
              interestRate: Number(loanDetails.interestRate).toString(),
              loanDuration: (Number(loanDetails.loanDuration) / 86400).toString(),
              isActive: loanDetails.isActive,
            });
          }
        }
      }

      setAvailableLoans(loans);
    } catch (err) {
      setError("Failed to fetch available loans: " + err.message);
      alert("Failed to fetch available loans");
    }
  };

  const fetchAllBorrowerLoans = async (contractInstance, provider) => {
    try {
      const fromBlock = 0;
      const toBlock = "latest";

      const activatedEvents = await contractInstance.queryFilter(
        contractInstance.filters.UserLoanActivated(),
        fromBlock,
        toBlock
      );

      const loans = [];
      for (const event of activatedEvents) {
        const { borrower } = event.args;
        if (!borrower) continue;

        const lender = await contractInstance.borrowerToLender(borrower);
        if (!lender || lender === "0x0000000000000000000000000000000000000000") continue;

        const loanDetails = await contractInstance.userLoans(lender);
        if (
          loanDetails.isActive &&
          loanDetails.lender !== "0x0000000000000000000000000000000000000000"
        ) {
          const loanData = {
            lender: loanDetails.lender,
            borrower: loanDetails.borrower,
            amount: formatEther(loanDetails.loanAmount),
            collateral: formatEther(loanDetails.collateralTokens),
            interestRate: Number(loanDetails.interestRate).toString(),
            loanDuration: (Number(loanDetails.loanDuration) / 86400).toString(),
            startTime: Number(loanDetails.startTime),
          };
          if (!loans.some((l) => l.borrower.toLowerCase() === borrower.toLowerCase())) {
            loans.push(loanData);
          }
        }
      }

      setBorrowerLoans(loans);
    } catch (err) {
      setError("Failed to fetch borrower loans: " + err.message);
      alert("Failed to fetch borrower loans");
    }
  };

  const fetchUserBorrowerLoans = async (contractInstance, userAddress) => {
    try {
      const loanDetails = await contractInstance.getLoanByBorrower(userAddress);
      if (
        loanDetails.lender !== "0x0000000000000000000000000000000000000000" &&
        loanDetails.isActive
      ) {
        const loanData = {
          lender: loanDetails.lender,
          borrower: userAddress,
          amount: formatEther(loanDetails.loanAmount),
          collateral: formatEther(loanDetails.collateralTokens),
          interestRate: Number(loanDetails.interestRate).toString(),
          loanDuration: (Number(loanDetails.loanDuration) / 86400).toString(),
          startTime: Number(loanDetails.startTime),
        };
        setUserBorrowerLoans([loanData]);
      } else {
        setUserBorrowerLoans([]);
      }
    } catch (err) {
      console.error("No active loan found for user as borrower:", err);
      setUserBorrowerLoans([]);
    }
  };

  const fetchLenderLoans = async (contractInstance, userAddress, provider) => {
    try {
      const fromBlock = 0;
      const toBlock = "latest";

      const activatedEvents = await contractInstance.queryFilter(
        contractInstance.filters.UserLoanActivated(),
        fromBlock,
        toBlock
      );

      const loans = [];
      for (const event of activatedEvents) {
        const { borrower } = event.args;
        const lender = await contractInstance.borrowerToLender(borrower);
        if (
          !lender ||
          lender === "0x0000000000000000000000000000000000000000" ||
          lender.toLowerCase() !== userAddress.toLowerCase()
        ) {
          continue;
        }

        const loanDetails = await contractInstance.userLoans(lender);
        if (
          loanDetails.isActive &&
          loanDetails.lender !== "0x0000000000000000000000000000000000000000"
        ) {
          const loanData = {
            lender: loanDetails.lender,
            borrower: loanDetails.borrower,
            amount: formatEther(loanDetails.loanAmount),
            collateral: formatEther(loanDetails.collateralTokens),
            interestRate: Number(loanDetails.interestRate).toString(),
            loanDuration: (Number(loanDetails.loanDuration) / 86400).toString(),
            startTime: Number(loanDetails.startTime),
          };
          if (!loans.some((l) => l.borrower.toLowerCase() === borrower.toLowerCase())) {
            loans.push(loanData);
          }
        }
      }

      setLenderLoans(loans);
    } catch (err) {
      setError("Failed to fetch lender loans: " + err.message);
      alert("Failed to fetch lender loans");
    }
  };

  const calculateTotalRepayment = (loan, time = currentTime) => {
    try {
      const elapsedSeconds = time - loan.startTime;
      if (elapsedSeconds < 0) return loan.amount; // Prevent negative interest
      const loanAmount = parseEther(loan.amount);
      const annualInterestRate = parseFloat(loan.interestRate);
      const totalInterest =
        (BigInt(loanAmount) * BigInt(Math.floor(annualInterestRate)) * BigInt(elapsedSeconds)) /
        (BigInt(365 * 24 * 3600) * BigInt(100));
      const totalRepayment = BigInt(loanAmount) + totalInterest;
      return formatEther(totalRepayment);
    } catch (err) {
      console.error("Error calculating total repayment:", err);
      return "0";
    }
  };

  const handleCreateLoan = async () => {
    if (!contract || !amount || !interestRate || !loanDuration) {
      setError("Please fill in all fields and ensure contract is connected.");
      alert("Please fill in all fields");
      return;
    }

    try {
      const amountWei = parseEther(amount);
      const annualInterestRate = parseFloat(interestRate);
      if (annualInterestRate < 0) {
        setError("Interest rate cannot be negative");
        alert("Interest rate cannot be negative");
        return;
      }
      const durationSeconds = Number(loanDuration) * 86400;
      const maxDuration = await contract.maxLoanDuration();
      if (durationSeconds > Number(maxDuration)) {
        setError(`Loan duration cannot exceed ${Number(maxDuration) / 86400} days`);
        alert("Loan duration exceeds maximum allowed duration");
        return;
      }

      const balance = await contract.balanceOf(userAddress);
      if (BigInt(balance) < BigInt(amountWei)) {
        setError("Insufficient CRW balance to create loan");
        alert("Insufficient CRW balance");
        return;
      }

      const contractInterestRate = Math.floor(annualInterestRate);

      const tx = await contract.createUserLoan(
        amountWei,
        contractInterestRate,
        durationSeconds
      );
      await tx.wait();
      alert("Loan created successfully!");
      setAmount("");
      setInterestRate("");
      setLoanDuration("");
      setError("");
    } catch (err) {
      setError("Error creating loan: " + err.message);
      alert("Error creating loan: " + err.message);
    }
  };

  const handleActivateLoan = async (lender, requiredCollateral) => {
    if (!contract || !requiredCollateral) {
      setError("Please ensure contract is connected and collateral is specified.");
      alert("Missing contract or collateral");
      return;
    }

    try {
      const collateralWei = parseEther(requiredCollateral);
      const balance = await contract.balanceOf(userAddress);
      if (BigInt(balance) < BigInt(collateralWei)) {
        setError("Insufficient CRW balance for collateral");
        alert("Insufficient CRW balance");
        return;
      }

      const approveTx = await contract.approve(contract.target, collateralWei);
      await approveTx.wait();
      alert("CRW tokens approved");

      const tx = await contract.activateUserLoan(lender, collateralWei);
      await tx.wait();
      alert("Loan activated successfully!");
      setError("");
    } catch (err) {
      setError("Error activating loan: " + err.message);
      alert("Error activating loan: " + err.message);
    }
  };

  const handleRepayLoan = async (lender) => {
    if (!contract) {
      setError("Contract is not connected.");
      alert("Contract not connected");
      return;
    }

    try {
      const loanDetails = await contract.getLoanByBorrower(userAddress);
      if (loanDetails.lender.toLowerCase() !== lender.toLowerCase()) {
        setError("No active loan found for this lender");
        alert("No active loan found for this lender");
        return;
      }

      const elapsedSeconds = currentTime - Number(loanDetails.startTime);
      const loanAmountWei = BigInt(loanDetails.loanAmount);
      const annualInterestRate = Number(loanDetails.interestRate);
      const totalInterest =
        (loanAmountWei * BigInt(annualInterestRate) * BigInt(elapsedSeconds)) /
        (BigInt(365 * 24 * 3600) * BigInt(100));
      const totalRepayment = loanAmountWei + totalInterest;

      const balance = await contract.balanceOf(userAddress);
      if (BigInt(balance) < totalRepayment) {
        setError("Insufficient CRW balance to repay loan");
        alert("Insufficient CRW balance");
        return;
      }

      const approveTx = await contract.approve(contract.target, totalRepayment);
      await approveTx.wait();
      alert("CRW tokens approved for repayment");

      const tx = await contract.repayUserLoan();
      await tx.wait();
      alert("Loan repaid successfully!");
      setError("");
    } catch (err) {
      setError("Error repaying loan: " + err.message);
      alert("Error repaying loan: " + err.message);
    }
  };

  const handleLiquidateLoan = async (borrower) => {
    if (!contract) {
      setError("Contract is not connected.");
      alert("Contract not connected");
      return;
    }

    try {
      const loanDetails = await contract.userLoans(borrower);
      if (loanDetails.lender.toLowerCase() !== userAddress.toLowerCase()) {
        setError("You are not the lender of this loan");
        alert("You are not the lender");
        return;
      }
      if (currentTime <= Number(loanDetails.startTime) + Number(loanDetails.loanDuration)) {
        setError("Loan has not expired yet");
        alert("Loan has not expired");
        return;
      }

      const tx = await contract.liquidateUserLoanByLender(borrower);
      await tx.wait();
      alert("Loan liquidated successfully!");
      setError("");
    } catch (err) {
      setError("Error liquidating loan: " + err.message);
      alert("Error liquidating loan: " + err.message);
    }
  };

  const handleCancelLoan = async () => {
    if (!contract) {
      setError("Contract is not connected.");
      alert("Contract not connected");
      return;
    }

    try {
      const loanDetails = await contract.userLoans(userAddress);
      if (loanDetails.lender.toLowerCase() !== userAddress.toLowerCase()) {
        setError("You are not the lender of an active loan");
        alert("You are not the lender");
        return;
      }

      const tx = await contract.cancelUserLoan();
      await tx.wait();
      alert("Loan cancelled successfully!");
      setError("");
    } catch (err) {
      setError("Error cancelling loan: " + err.message);
      alert("Error cancelling loan: " + err.message);
    }
  };

  const handleRefresh = async () => {
    if (!contract || !provider || !userAddress) return;
    try {
      await Promise.all([
        fetchAvailableLoans(contract, provider),
        fetchAllBorrowerLoans(contract, provider),
        fetchLenderLoans(contract, userAddress, provider),
        fetchUserBorrowerLoans(contract, userAddress),
      ]);
      alert("Loans refreshed successfully!");
    } catch (err) {
      setError("Failed to refresh loans: " + err.message);
      alert("Failed to refresh loans");
    }
  };

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const handleInterestRateChange = (e) => {
    setInterestRate(e.target.value);
  };

  const handleLoanDurationChange = (e) => {
    setLoanDuration(e.target.value);
  };

  return (
    <div className="main-content">
      <h2>User Loan Actions</h2>
      <div className="user-info">
        <p>Collateral: 130%</p>
        <p>CRW Balance: {userBalance} CRW</p>
        <button onClick={handleRefresh}>Refresh Loans</button>
      </div>
      <div className="tabs">
        <div
          className={`tab ${activeTab === "create" ? "active" : ""}`}
          onClick={() => setActiveTab("create")}
        >
          Create Loan
        </div>
        <div
          className={`tab ${activeTab === "borrower" ? "active" : ""}`}
          onClick={() => setActiveTab("borrower")}
        >
          Borrower Loans
        </div>
        <div
          className={`tab ${activeTab === "repay" ? "active" : ""}`}
          onClick={() => setActiveTab("repay")}
        >
          Repay User Loan
        </div>
        <div
          className={`tab ${activeTab === "lender" ? "active" : ""}`}
          onClick={() => setActiveTab("lender")}
        >
          Lender Loans
        </div>
        <div
          className={`tab ${activeTab === "available" ? "active" : ""}`}
          onClick={() => setActiveTab("available")}
        >
          Available User Loans
        </div>
      </div>

      {activeTab === "create" && (
        <div className="action-box">
          <h3>Create New Loan</h3>
          <input
            type="number"
            id="loan-amount"
            name="loan-amount"
            placeholder="Loan Amount (CRW)"
            min="0"
            step="any"
            value={amount}
            onChange={handleAmountChange}
          />
          <input
            type="number"
            id="interest-rate"
            name="interest-rate"
            placeholder="Rate % per Duration"
            min="0"
            step="1"
            value={interestRate}
            onChange={handleInterestRateChange}
          />
          <input
            type="number"
            id="loan-duration"
            name="loan-duration"
            placeholder="Loan Duration (days)"
            min="1"
            step="1"
            value={loanDuration}
            onChange={handleLoanDurationChange}
          />
          <button onClick={handleCreateLoan}>Create Loan</button>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      {activeTab === "borrower" && (
        <div className="action-box">
          <h3>Borrower Loans</h3>
          <table>
            <thead>
              <tr>
                <th>Lender</th>
                <th>Borrower</th>
                <th>Amount (CRW)</th>
                <th>Collateral (CRW)</th>
                <th>Rate % per Duration</th>
                <th>Duration (days)</th>
                <th>Total Repayment (CRW)</th>
              </tr>
            </thead>
            <tbody>
              {borrowerLoans.map((loan, index) => (
                <tr key={index}>
                  <td>{loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</td>
                  <td>{loan.borrower.slice(0, 6)}...{loan.borrower.slice(-4)}</td>
                  <td>{loan.amount}</td>
                  <td>{loan.collateral}</td>
                  <td>{loan.interestRate}</td>
                  <td>{loan.loanDuration}</td>
                  <td>{calculateTotalRepayment(loan)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {borrowerLoans.length === 0 && <p>No active borrower loans found.</p>}
        </div>
      )}

      {activeTab === "repay" && (
        <div className="action-box">
          <h3>Repay User Loan</h3>
          <table>
            <thead>
              <tr>
                <th>Lender</th>
                <th>Borrower</th>
                <th>Amount (CRW)</th>
                <th>Collateral (CRW)</th>
                <th>Rate % per Duration</th>
                <th>Duration (days)</th>
                <th>Start Time</th>
                <th>Total Repayment (CRW)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userBorrowerLoans.map((loan, index) => (
                <tr key={index}>
                  <td>{loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</td>
                  <td>{loan.borrower.slice(0, 6)}...{loan.borrower.slice(-4)}</td>
                  <td>{loan.amount}</td>
                  <td>{loan.collateral}</td>
                  <td>{loan.interestRate}</td>
                  <td>{loan.loanDuration}</td>
                  <td>{new Date(loan.startTime * 1000).toLocaleString()}</td>
                  <td>{calculateTotalRepayment(loan)}</td>
                  <td>
                    <button onClick={() => handleRepayLoan(loan.lender)}>Repay User Loan</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {userBorrowerLoans.length === 0 && <p>You have no active loans to repay.</p>}
        </div>
      )}

      {activeTab === "lender" && (
        <div className="action-box">
          <h3>Lender Loans</h3>
          <table>
            <thead>
              <tr>
                <th>Lender</th>
                <th>Borrower</th>
                <th>Amount (CRW)</th>
                <th>Collateral (CRW)</th>
                <th>Rate % per Duration</th>
                <th>Duration (days)</th>
                <th>Start Time</th>
                <th>Total Repayment (CRW)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lenderLoans.map((loan, index) => (
                <tr key={index}>
                  <td>{loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</td>
                  <td>{loan.borrower.slice(0, 6)}...{loan.borrower.slice(-4)}</td>
                  <td>{loan.amount}</td>
                  <td>{loan.collateral}</td>
                  <td>{loan.interestRate}</td>
                  <td>{loan.loanDuration}</td>
                  <td>{new Date(loan.startTime * 1000).toLocaleString()}</td>
                  <td>{calculateTotalRepayment(loan)}</td>
                  <td>
                    {process.env.NODE_ENV === "development" ||
                    currentTime > loan.startTime + Number(loan.loanDuration) * 86400 ? (
                      <button onClick={() => handleLiquidateLoan(loan.borrower)}>
                        Liquidate User Loan
                      </button>
                    ) : (
                      <span>Loan not expired</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {lenderLoans.length === 0 && <p>You have no active lender loans.</p>}
        </div>
      )}

      {activeTab === "available" && (
        <div className="action-box">
          <h3>Available User Loans</h3>
          <table>
            <thead>
              <tr>
                <th>Lender</th>
                <th>Amount (CRW)</th>
                <th>Collateral (CRW)</th>
                <th>Rate % per Duration</th>
                <th>Loan Duration (days)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {availableLoans
                .filter((loan) => !loan.isActive)
                .map((loan, index) => (
                  <tr key={index}>
                    <td>{loan.lender.slice(0, 6)}...{loan.lender.slice(-4)}</td>
                    <td>{loan.amount}</td>
                    <td>{loan.requiredCollateral}</td>
                    <td>{loan.interestRate}</td>
                    <td>{loan.loanDuration}</td>
                    <td>{loan.isActive ? "Active" : "Inactive"}</td>
                    <td>
                      <button
                        onClick={() => handleActivateLoan(loan.lender, loan.requiredCollateral)}
                      >
                        Activate
                      </button>
                      <button
                        onClick={handleCancelLoan}
                        disabled={loan.lender.toLowerCase() !== userAddress.toLowerCase()}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {error && <p className="error">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default UserLoanActions;